import { useCallback, useEffect, useRef, useState } from 'react';
import { PollingConfig, PollingState, PollingStatusFilter, PollingUpdateCallback, PollingFetchFunction } from '../types/polling';

interface UsePollingProps<T> {
  items: T[];
  config: PollingConfig;
  statusFilter: PollingStatusFilter<T>;
  fetchFunction: PollingFetchFunction<T>;
  onUpdate: PollingUpdateCallback<T>;
}

interface UsePollingReturn {
  start: () => void;
  stop: () => void;
  state: PollingState;
}

export const usePolling = <T extends { id: string }>({
  items,
  config,
  statusFilter,
  fetchFunction,
  onUpdate,
}: UsePollingProps<T>): UsePollingReturn => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const itemsRef = useRef(items);
  const onUpdateRef = useRef(onUpdate);
  const isProcessingRef = useRef(false);
  const attemptCountRef = useRef(0);
  const [state, setState] = useState<PollingState>({
    isActive: false,
    lastUpdate: null,
    errorCount: 0,
    attemptCount: 0,
  });

  itemsRef.current = items;
  onUpdateRef.current = onUpdate;
  const stop = useCallback((): void => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setState(prevState => ({ ...prevState, isActive: false }));
    }
  }, []);
  const processUpdates = useCallback(async (): Promise<void> => {
    if (!config.enabled || isProcessingRef.current) return;

    isProcessingRef.current = true;

    // Increment attempt count using ref and check maxAttempts limit
    attemptCountRef.current += 1;
    const maxAttempts = config.maxAttempts ?? Infinity;

    // Update state with current attempt count for external access
    setState(prevState => ({
      ...prevState,
      attemptCount: attemptCountRef.current,
    }));

    if (attemptCountRef.current >= maxAttempts) {
      setTimeout(() => {
        stop();
      }, 0);
      isProcessingRef.current = false;
      return;
    }

    try {
      const currentItems = itemsRef.current;
      const targetItems = currentItems.filter(statusFilter);

      if (targetItems.length === 0) {
        isProcessingRef.current = false;
        return;
      }

      const updatedItems = await Promise.all(
        targetItems.map(async (item) => {
          try {
            return await fetchFunction(item);
          } catch {
            throw new Error('Item fetch failed');
          }
        }));

      // Reset error count on successful fetch (regardless of whether items changed)
      setState(prevState => ({
        ...prevState,
        errorCount: 0,
        lastUpdate: new Date(),
      }));

      const changedItems = updatedItems.filter((updated, index) =>
        JSON.stringify(updated) !== JSON.stringify(targetItems[index])
      );

      if (changedItems.length === 0) {
        isProcessingRef.current = false;
        return;
      }

      const finalItems = currentItems.map(item => {
        const updated = updatedItems.find(u => u.id === item.id);
        return updated || item;
      });

      onUpdateRef.current(finalItems);
      isProcessingRef.current = false;
    } catch (error) {
      setState(prevState => {
        const newErrorCount = prevState.errorCount + 1;
        const maxRetries = config.maxRetries ?? Infinity;

        // Use setTimeout to avoid React warning about stopping during render
        if (newErrorCount >= maxRetries) {
          setTimeout(() => {
            stop();
          }, 0);
        }

        return {
          ...prevState,
          errorCount: newErrorCount,
        };
      });
      isProcessingRef.current = false;
    }
  }, [config.enabled, config.maxRetries, config.maxAttempts, statusFilter, fetchFunction, stop]);

  const start = useCallback((): void => {
    if (intervalRef.current || !config.enabled) {
      return;
    }

    // Reset attempt count when starting
    attemptCountRef.current = 0;
    setState(prevState => ({ ...prevState, attemptCount: 0 }));

    intervalRef.current = setInterval(processUpdates, config.interval);
    setState(prevState => ({ ...prevState, isActive: true }));
  }, [config.enabled, config.interval, config.maxRetries, config.maxAttempts, processUpdates]);

  useEffect(() => {
    const hasTargetItems = items.some(statusFilter);

    if (config.enabled && hasTargetItems) {
      start();
    } else {
      stop();
    }

    return stop;
  }, [items, config.enabled, statusFilter, start, stop]);

  return {
    start,
    stop,
    state,
  };
};
